import { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router';
import { IconAlert } from '@belong/icons';
import { Button, Tooltip } from '@belong/ui';
import {
  ListingApplicationApplicantModel,
  ListingApplicationListingApplicationModel,
} from 'api/models';
import clsx from 'clsx';
import ApplicationApplicantList from 'containercomponents/ApplicationApplicantList/ApplicationApplicantList';
import ApplicationSignerList from 'containercomponents/ApplicationSignerList.tsx/ApplicationSignerList';
import ApplicationVerificationsStatus from 'containercomponents/ApplicationVerificationsStatus/ApplicationVerificationsStatus';
import { LeaseAdvisorContactData } from 'containercomponents/LeaseAdvisorContactData/LeaseAdvisorContactData';
import { ScreenResult } from 'pages/Accounts/Pages/ToursAndApps/ApplicationStatusUtils';

interface NoticeBannerProps {
  applicationScreen: ScreenResult;
  // All four are optional at the one call site — the application details, the
  // applicant and the grayscale flag all come off data that may not have loaded —
  // and the reads below already treat them that way.
  listingApplicationDetails?: ListingApplicationListingApplicationModel;
  currentApplicant?: ListingApplicationApplicantModel;
  isGrayscale?: boolean;
  leaseAdvisor?: LeaseAdvisor;
  currentListingApplicant?: ListingApplicationApplicantModel;
}

interface LeaseAdvisorTooltipProps {
  name: string;
  phone?: string;
  email?: string;
  avatarUrl: string;
}

export type LeaseAdvisor = {
  employeeAssignment: {
    sourceType: string;
    sourceUniqueId: string;
    assignmentType: string;
  };
  employee: {
    bannerImageUrl: string;
    workPhone: string;
    workEmail: string;
  };
  user: {
    userId: string;
    name: string;
    firstName: string;
    lastName: string;
    profileImageUrl: string;
    memberSince: string; // ISO datetime string
    aboutMe: string;
  };
};

function LeaseAdvisorTooltip({
  name,
  phone,
  email,
  avatarUrl,
}: LeaseAdvisorTooltipProps) {
  return (
    <div className="h-full w-[274px] rounded-[12px] border-none bg-darker-navy p-sm text-white">
      <div className="flex flex-col items-center gap-xs text-center">
        <div className="text-p3 font-semibold">
          Contact your Leasing&nbsp;Advisor
        </div>
        <div className="relative">
          <div className="p-2 rounded-full border-4 border-dark-green bg-green">
            <img
              src={avatarUrl}
              alt={name}
              className="h-xl w-xl rounded-full object-cover ring-4 ring-green"
            />
          </div>
        </div>

        <div className="flex flex-col items-center gap-0">
          <div className="text-p3 mb-0 pb-0 font-semibold">{name}</div>
        </div>
        <div className="flex flex-col gap-0">
          {phone && (
            <div className="text-p1 font-semibold">
              {phone
                ? phone.replace(/\D/g, '').length === 10
                  ? `(${phone.replace(/\D/g, '').slice(0, 3)}) ${phone.replace(/\D/g, '').slice(3, 6)}-${phone
                      .replace(/\D/g, '')
                      .slice(6)}`
                  : phone
                : ''}
            </div>
          )}

          {email && (
            <div className="break-words text-p1 font-semibold">{email}</div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function ApplicationInfoCard({
  applicationScreen,
  listingApplicationDetails,
  currentApplicant,
  isGrayscale,
  leaseAdvisor,
  currentListingApplicant,
}: NoticeBannerProps) {
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);

  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsTooltipOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const history = useHistory();
  const {
    cardTitle,
    cardDescription,
    showWarningIcon,
    cta1,
    cta2,
    showIncomeButton,
    showApplicantsStatus,
    showVerificationsStatus,
    showLeaseAdvisorContact,
    signers,
  } = applicationScreen;

  // `.filter(Boolean)` does not narrow — the predicate is what drops the absent
  // CTAs, so the `action.onClick` call below is checked.
  const actions = [cta1, cta2].filter((cta): cta is NonNullable<typeof cta> =>
    Boolean(cta),
  );

  if (
    !showIncomeButton &&
    !showApplicantsStatus &&
    !showVerificationsStatus &&
    !showLeaseAdvisorContact &&
    !cardTitle &&
    !cardDescription
  ) {
    return null;
  }

  return (
    <div
      className={clsx(
        'border-1 w-full rounded-DEFAULT rounded-b border border-solid border-gray bg-light-gray p-sm',
        'flex flex-col items-start gap-xs',
      )}
      role="alert"
      aria-live="polite"
    >
      {(cardDescription || cardTitle) && (
        <div className="min-w-0 flex-1">
          {cardTitle && (
            <div
              className={clsx(
                'text-p3 mb-xs flex flex-col items-start gap-2xs font-semibold tracking-lg md:flex-row',
                isGrayscale ? 'text-dark-gray' : 'text-navy',
              )}
            >
              {showWarningIcon && (
                <IconAlert className="w-5 h-5 mr-2xs" width={24} height={24} />
              )}
              {cardTitle}
            </div>
          )}
          {cardDescription && (
            <div
              className={clsx(
                'mt-2xs text-p1 leading-[1.2]',
                isGrayscale ? 'text-dark-gray' : 'text-navy',
              )}
            >
              {cardDescription}
            </div>
          )}
        </div>
      )}
      {(showIncomeButton || (actions && actions.length > 0)) && (
        <div className="flex w-full flex-col justify-end gap-sm py-xs md:flex-row">
          {showIncomeButton && leaseAdvisor && (
            <Tooltip
              arrowProps={{
                className: 'fill-darker-navy',
              }}
              contentProps={{
                className: 'rounded-[12px] shadow-md z-200',
              }}
              open={isTooltipOpen}
              portal
              content={
                <div>
                  <LeaseAdvisorTooltip
                    name={leaseAdvisor.user.name}
                    phone={leaseAdvisor.employee.workPhone}
                    email={leaseAdvisor.employee.workEmail}
                    avatarUrl={leaseAdvisor.user.profileImageUrl}
                  />
                </div>
              }
            >
              <Button
                ref={buttonRef}
                variant="solid"
                className="w-full"
                onClick={() => setIsTooltipOpen((prev) => !prev)}
              >
                Add More Income
              </Button>
            </Tooltip>
          )}
          {actions &&
            actions.length > 0 &&
            actions.map((action, i) => {
              return (
                <Button
                  key={i}
                  variant="solid"
                  className="w-full"
                  onClick={() => action.onClick(history.push)}
                >
                  {action.label}
                </Button>
              );
            })}
        </div>
      )}

      {showApplicantsStatus && (
        <ApplicationApplicantList
          applicants={listingApplicationDetails?.application?.applicants || []}
          kids={listingApplicationDetails?.application?.kids || []}
          pets={listingApplicationDetails?.application?.applicationPets || []}
          currentListingApplicant={currentListingApplicant}
        />
      )}
      {signers && <ApplicationSignerList signers={signers} />}
      {showVerificationsStatus && (
        <ApplicationVerificationsStatus
          items={currentApplicant?.verifications || []}
        />
      )}

      {showLeaseAdvisorContact && leaseAdvisor && (
        <LeaseAdvisorContactData
          name={leaseAdvisor.user.name}
          phone={leaseAdvisor.employee.workPhone}
          email={leaseAdvisor.employee.workEmail}
          description="Your Leasing Advisor"
          avatarUrl={leaseAdvisor.user.profileImageUrl}
        />
      )}
    </div>
  );
}
